// status-progress-status-label-classes.ts
//
// Progress panel P1 (B4): the EXPLICIT map from a mission status label to its
// effect on the journey — every label the writers can journal is named here
// (status-progress-mission-status-label-coverage.test.ts enumerates them from
// the writers), so only a truly foreign label reaches `unrecognized` and is
// shown as `unknown` rather than guessed (SU R7). Pure.
import { RUN_OUTCOME_STATUS_LABELS } from "./mission-status-label-after-run.js";

export type StatusLabelEffect =
  /** Absent label (new mission), legacy RUNNING, `done`, `blocked:gate_not_approved`. */
  | { kind: "none" }
  /** Run-ending labels that keep the journey state; `summary` for quota sleep. */
  | { kind: "keep"; summary?: string }
  | { kind: "aborted" }
  | { kind: "integration"; state: "active" | "blocked" }
  /** `waiting_gate:g3` — integration finished, G3 from gates.json. */
  | { kind: "integration_done" }
  | { kind: "release"; state: "done" | "blocked"; summary?: string }
  | { kind: "gate_unknown" }
  | { kind: "interview" }
  | { kind: "plan_not_ready" }
  | { kind: "cost_ceiling"; text: string }
  | { kind: "active_blocked"; code: string }
  | { kind: "unrecognized" };

const RELEASE_BLOCKED_LABELS = new Set([
  "blocked:deploy",
  "blocked:deploy_smoke_failed",
  "blocked:deploy_outcome_unknown",
  "blocked:release_g3_invalid",
  "blocked:release_unverified_legacy",
  "blocked:release_secrets_stdin_required",
]);
const INTEGRATION_BLOCKED_LABELS = new Set([
  "blocked:release_ci_failed",
  "blocked:release_ci_not_passed",
  "blocked:release_flow",
]);
const COST_CEILING_TEXT: Record<string, string> = {
  "blocked:cost_limit_reached": "chạm trần chi phí",
  "blocked:attempt_limit_reached": "chạm trần số attempt",
  // Journals from before the two ceiling paths converged (2026-09-26) end here.
  WAITING_HUMAN: "chạm trần chi phí/số attempt",
};
const NONE: StatusLabelEffect = { kind: "none" };

export function classifyStatusLabel(label: string | undefined): StatusLabelEffect {
  if (label === undefined || label === "RUNNING" || label === "done") return NONE;
  if (label === "blocked:gate_not_approved") return NONE;
  if (label === "ABORTED") return { kind: "aborted" };
  if (label === "quota_sleeping") return { kind: "keep", summary: "chờ quota" };
  if (["blocked", "idle", "interrupted", "paused"].includes(label)) return { kind: "keep" };
  if (label === "waiting_ci") return { kind: "integration", state: "active" };
  if (INTEGRATION_BLOCKED_LABELS.has(label)) return { kind: "integration", state: "blocked" };
  if (label === "waiting_gate:g3") return { kind: "integration_done" };
  if (label === "released") return { kind: "release", state: "done" };
  if (label === "done_without_deploy") {
    return { kind: "release", state: "done", summary: "không deploy" };
  }
  if (RELEASE_BLOCKED_LABELS.has(label))
    return { kind: "release", state: "blocked", summary: label };
  if (label === "blocked:gate_state_unreadable") return { kind: "gate_unknown" };
  if (label === "blocked:interview_unanswered") return { kind: "interview" };
  if (label === "blocked:g2_plan_not_ready") return { kind: "plan_not_ready" };
  const ceiling = COST_CEILING_TEXT[label];
  if (ceiling !== undefined) return { kind: "cost_ceiling", text: ceiling };
  // Before-dispatch / ops-check codes (quiet_hours, pin_drift, …, before_dispatch).
  if (label.startsWith("blocked:")) return { kind: "active_blocked", code: label.slice(8) };
  return { kind: "unrecognized" };
}

/**
 * B17 "nhãn cũ": a stop/wait label later work may have outlived. Never
 * ABORTED (sticky, terminal) nor the release-flow labels (review candidate
 * reruns run AFTER them on purpose), nor `paused` (read live from pause-state.json).
 */
export function isStaleableLabel(label: string | undefined): boolean {
  if (label === undefined) return false;
  const effect = classifyStatusLabel(label);
  switch (effect.kind) {
    case "keep":
      return label !== "paused";
    case "gate_unknown":
    case "interview":
    case "plan_not_ready":
    case "cost_ceiling":
    case "active_blocked":
      return true;
    default:
      return label === "blocked:gate_not_approved";
  }
}

/**
 * B17 "nhãn cũ" rule: a staleable label is old when work was journaled after
 * it (seq) or a live supervisor has a ticket in progress. Run-outcome labels
 * (`blocked`/`idle`/`quota_sleeping`) take only the liveness half: a later
 * run that ends the same way does not re-journal the label
 * (mission-status-label-after-run.ts `next === currentStatus`), so its
 * attempts sit after a still-current label.
 */
export function isStatusLabelStale(input: {
  label: string | undefined;
  lastStatusLabelSeq: number;
  lastWorkSeq: number;
  supervisorAlive: boolean;
  anyRunning: boolean;
}): boolean {
  if (!isStaleableLabel(input.label)) return false;
  if (input.supervisorAlive && input.anyRunning) return true;
  if (RUN_OUTCOME_STATUS_LABELS.includes(input.label as string)) return false;
  return input.lastStatusLabelSeq < input.lastWorkSeq;
}
